/**
 * AdminUsersPage Component
 * Admin page for managing users
 *
 * - Shows current logged-in user in the list but disables actions
 * - Visual indicator (badge) for "You" row
 * - Disabled toggle and role change for current user
 * - Tooltip explaining why actions are disabled
 */

import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAdminUsers, updateUserRole, updateUserStatus } from "../api/admin";
import { AdminUser, AdminUserFilters, AuthProvider } from "../types";
import { AppDispatch, RootState } from "../redux/store";
import { addNotification } from "../redux/slices/uiSlice";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { Select } from "../components/common/Select";
import { Spinner } from "../components/common/Spinner";
import { Badge } from "../components/common/Badge";
import { Modal } from "../components/common/Modal";
import { formatDate, formatFileSize } from "../utils/format.utils";

export const AdminUsersPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Get current logged-in user from auth state
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // Filters
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [authProviderFilter, setAuthProviderFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  // Modal state for role change confirmation
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState<"user" | "admin">("user");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const filters: AdminUserFilters = {
        page,
        limit: 20,
      };

      if (roleFilter) filters.role = roleFilter as "user" | "admin";
      if (authProviderFilter)
        filters.auth_provider = authProviderFilter as AuthProvider;
      if (statusFilter) filters.is_active = statusFilter === "active";

      const data = await getAdminUsers(filters);
      setUsers(data.users);
      setTotal(data.total);
      setPages(data.pages);
    } catch (error: any) {
      dispatch(
        addNotification({
          type: "error",
          message: error.response?.data?.detail || "Failed to fetch users",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [page, roleFilter, authProviderFilter, statusFilter, dispatch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /**
   * Check if user is the current logged-in user
   */
  const isCurrentUser = (user: AdminUser): boolean => {
    return currentUser?.id === user.id;
  };

  // Handle role change with confirmation
  const handleRoleClick = (user: AdminUser) => {
    // Prevent action for current user
    if (isCurrentUser(user)) return;

    setSelectedUser(user);
    setNewRole(user.role === "admin" ? "user" : "admin");
    setShowRoleModal(true);
  };

  const confirmRoleChange = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      await updateUserRole(selectedUser.id, newRole);
      dispatch(
        addNotification({
          type: "success",
          message: `User role updated to ${newRole}`,
        })
      );
      setShowRoleModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error: any) {
      dispatch(
        addNotification({
          type: "error",
          message: error.response?.data?.detail || "Failed to update role",
        })
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (user: AdminUser) => {
    // Prevent action for current user
    if (isCurrentUser(user)) return;

    setActionLoading(true);
    try {
      await updateUserStatus(user.id, !user.is_active);
      dispatch(
        addNotification({
          type: "success",
          message: `User ${
            !user.is_active ? "activated" : "deactivated"
          } successfully`,
        })
      );
      fetchUsers();
    } catch (error: any) {
      dispatch(
        addNotification({
          type: "error",
          message: error.response?.data?.detail || "Failed to update status",
        })
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Get auth provider badge styling
  const getAuthProviderBadge = (provider: AuthProvider) => {
    const config: Record<
      AuthProvider,
      { variant: "info" | "error" | "neutral"; label: string }
    > = {
      email: { variant: "info", label: "Email" },
      google: { variant: "error", label: "Google" },
      face: { variant: "neutral", label: "Face" },
    };
    return config[provider] || { variant: "neutral", label: provider };
  };

  // Get role badge styling
  const getRoleBadge = (role: string) => {
    if (role === "admin") {
      return { variant: "warning" as const, label: "Admin" };
    }
    return { variant: "info" as const, label: "User" };
  };

  return (
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <p className="text-slate-400 mt-1">{total} total users</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select
              label="Role"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: "", label: "All Roles" },
                { value: "user", label: "User" },
                { value: "admin", label: "Admin" },
              ]}
            />
            <Select
              label="Auth Method"
              value={authProviderFilter}
              onChange={(e) => {
                setAuthProviderFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: "", label: "All Methods" },
                { value: "email", label: "Email" },
                { value: "google", label: "Google" },
                { value: "face", label: "Face" },
              ]}
            />
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: "", label: "All Status" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
            />
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-400">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    User
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    Auth
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    Role
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    Jobs
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    Storage
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    Joined
                  </th>
                  <th className="text-right py-3 px-4 text-slate-400 font-medium text-sm">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const authBadge = getAuthProviderBadge(user.auth_provider);
                  const roleBadge = getRoleBadge(user.role);
                  const isSelf = isCurrentUser(user);

                  return (
                    <tr
                      key={user.id}
                      className={`
                        border-b border-slate-700/50 
                        ${
                          isSelf
                            ? "bg-cyan-500/5 hover:bg-cyan-500/10"
                            : "hover:bg-slate-700/30"
                        }
                      `}
                    >
                      {/* User Info */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium">
                                {user.full_name}
                              </p>
                              {isSelf && (
                                <Badge variant="info" size="sm">
                                  You
                                </Badge>
                              )}
                            </div>
                            <p className="text-slate-400 text-sm">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Auth Provider - Badge */}
                      <td className="py-3 px-4">
                        <Badge variant={authBadge.variant} size="sm">
                          {authBadge.label}
                        </Badge>
                      </td>

                      {/* Role - Badge (clickable to change, disabled for self) */}
                      <td className="py-3 px-4">
                        {isSelf ? (
                          <div className="relative group">
                            <Badge
                              variant={roleBadge.variant}
                              size="sm"
                              className="opacity-60 cursor-not-allowed"
                            >
                              {roleBadge.label}
                            </Badge>
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                              Cannot change own role
                            </span>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleRoleClick(user)}
                            disabled={actionLoading}
                            className="group relative"
                            title="Click to change role"
                          >
                            <Badge
                              variant={roleBadge.variant}
                              size="sm"
                              className="cursor-pointer hover:opacity-80 transition-opacity"
                            >
                              {roleBadge.label}
                            </Badge>
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                              Click to change
                            </span>
                          </button>
                        )}
                      </td>

                      {/* Status - Badge */}
                      <td className="py-3 px-4">
                        <Badge
                          variant={user.is_active ? "success" : "error"}
                          size="sm"
                          className={isSelf ? "opacity-60" : ""}
                        >
                          {user.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>

                      {/* Jobs Count */}
                      <td className="py-3 px-4 text-slate-300">
                        {user.job_count}
                      </td>

                      {/* Storage */}
                      <td className="py-3 px-4 text-slate-300">
                        {formatFileSize(user.total_storage_mb * 1024 * 1024)}
                      </td>

                      {/* Joined Date */}
                      <td className="py-3 px-4 text-slate-400 text-sm">
                        {formatDate(user.created_at)}
                      </td>

                      {/* Actions - Toggle Status Only */}
                      <td className="py-3 px-4">
                        <div className="flex justify-end">
                          {isSelf ? (
                            <div className="relative group">
                              <div
                                className={`
                                  relative inline-flex h-6 w-11 items-center rounded-full
                                  bg-green-500/50 opacity-50 cursor-not-allowed
                                `}
                              >
                                <span className="inline-block h-4 w-4 transform rounded-full bg-white/70 translate-x-6" />
                              </div>
                              <span className="absolute -top-8 right-0 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                Cannot change own status
                              </span>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleStatusToggle(user)}
                              disabled={actionLoading}
                              className={`
                                relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                                focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-800
                                ${
                                  user.is_active
                                    ? "bg-green-500"
                                    : "bg-slate-600"
                                }
                                ${
                                  actionLoading
                                    ? "opacity-50 cursor-not-allowed"
                                    : "cursor-pointer"
                                }
                              `}
                              title={
                                user.is_active
                                  ? "Click to deactivate"
                                  : "Click to activate"
                              }
                            >
                              <span
                                className={`
                                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                  ${
                                    user.is_active
                                      ? "translate-x-6"
                                      : "translate-x-1"
                                  }
                                `}
                              />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              Page {page} of {pages}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setPage(page + 1)}
                disabled={page === pages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Role Change Confirmation Modal */}
      <Modal
        isOpen={showRoleModal}
        onClose={() => {
          setShowRoleModal(false);
          setSelectedUser(null);
        }}
        title="Change User Role"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-slate-300">
            Are you sure you want to change{" "}
            <span className="text-white font-medium">
              {selectedUser?.full_name}
            </span>
            's role from{" "}
            <Badge
              variant={selectedUser?.role === "admin" ? "warning" : "info"}
              size="sm"
            >
              {selectedUser?.role}
            </Badge>{" "}
            to{" "}
            <Badge variant={newRole === "admin" ? "warning" : "info"} size="sm">
              {newRole}
            </Badge>
            ?
          </p>

          {newRole === "admin" && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/50 rounded-lg">
              <p className="text-yellow-400 text-sm">
                ⚠️ Admin users have full access to manage all users and view all
                jobs.
              </p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowRoleModal(false);
                setSelectedUser(null);
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant={newRole === "admin" ? "warning" : "primary"}
              onClick={confirmRoleChange}
              disabled={actionLoading}
            >
              {actionLoading ? <Spinner size="sm" /> : "Confirm Change"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
